import React, { useEffect, useRef, useState } from 'react';
import { useOS } from '../OSContext';
import { getApp } from '../apps';
import skills from '../../../data/skills';
import projects from '../../../data/projects';

const PROMPT_USER = 'visitor@sam-togun';
const VALID_APP_IDS = ['about', 'projects', 'resume', 'contact', 'terminal', 'settings'];

const BIO =
	"I'm Sam Togun, a Senior Backend Engineer & Solutions Architect. I specialize in building scalable, " +
	'reliable backend systems and cloud solutions for modern businesses. I architect and build scalable ' +
	'backend systems that power modern applications. With expertise in cloud technologies and microservices, ' +
	'I help businesses achieve reliability and performance at scale.';

const WELCOME_LINES = [
	'Last login: ' + new Date().toDateString(),
	"Sam Togun's portfolio terminal — type 'help' to see available commands.",
];

const HELP_LINES = [
	'Available commands:',
	'',
	'  help              Show this list of commands',
	'  whoami            Show who I am',
	'  about             Print a short bio',
	'  skills            List skills and proficiency levels',
	'  projects          List featured projects',
	'  contact           Show contact information',
	'  open <appid>      Open an app window',
	`                    (${VALID_APP_IDS.join(', ')})`,
	'  clear             Clear the screen',
];

function formatSkillLine(skill) {
	const label = skill.label.padEnd(38, ' ');
	const value = String(skill.value).padStart(3, ' ');
	return `${label} ${value}%`;
}

function formatProjectLines(project, index) {
	return [`${index + 1}. ${project.title}`, `   ${project.tags.join(', ')}`];
}

function renderLine(line, key) {
	if (line && typeof line === 'object') {
		return (
			<div key={key} className="whitespace-pre-wrap break-words">
				<a
					href={line.href}
					target="_blank"
					rel="noopener noreferrer"
					className="text-os-accent hover:underline"
				>
					{line.text}
				</a>
			</div>
		);
	}
	return (
		<div key={key} className="whitespace-pre-wrap break-words">
			{line === '' ? ' ' : line}
		</div>
	);
}

const TerminalApp = () => {
	const { openApp } = useOS();
	const [history, setHistory] = useState(() => [{ command: null, output: WELCOME_LINES }]);
	const [input, setInput] = useState('');
	const [cmdLog, setCmdLog] = useState([]);
	const [logPointer, setLogPointer] = useState(null);

	const scrollRef = useRef(null);
	const bottomRef = useRef(null);
	const inputRef = useRef(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: 'end' });
	}, [history]);

	const evaluateCommand = raw => {
		const parts = raw.trim().split(/\s+/);
		const cmd = parts[0].toLowerCase();
		const args = parts.slice(1);

		switch (cmd) {
			case 'help':
				return HELP_LINES;

			case 'whoami':
				return ['Sam Togun', 'Senior Backend Engineer & Solutions Architect', 'Stavanger, Norway'];

			case 'about':
				return [BIO];

			case 'skills':
				return ['Skills:', '', ...skills.map(formatSkillLine)];

			case 'projects':
				return [
					'Featured projects:',
					'',
					...projects.flatMap(formatProjectLines),
					'',
					"Type 'open projects' to view them in the Projects app.",
				];

			case 'contact':
				return [
					{ text: 'Email:     oluwatobitogun@gmail.com', href: 'mailto:oluwatobitogun@gmail.com' },
					{
						text: 'LinkedIn:  https://www.linkedin.com/in/oluwatobi-togun/',
						href: 'https://www.linkedin.com/in/oluwatobi-togun/',
					},
					{ text: 'GitHub:    https://github.com/TOGUNOLUWATOBI', href: 'https://github.com/TOGUNOLUWATOBI' },
				];

			case 'open': {
				const target = (args[0] || '').toLowerCase();
				if (!target) {
					return ['usage: open <appid>', `available apps: ${VALID_APP_IDS.join(', ')}`];
				}
				const app = getApp(target);
				if (!app) {
					return ['no such app'];
				}
				openApp(app);
				return [`Opening ${app.title}...`];
			}

			default:
				return [`command not found: ${cmd}`];
		}
	};

	const submitCommand = () => {
		const trimmed = input.trim();
		setLogPointer(null);

		if (!trimmed) {
			setHistory(prev => [...prev, { command: '', output: [] }]);
			setInput('');
			return;
		}

		setCmdLog(prev => [...prev, trimmed]);

		if (trimmed.toLowerCase() === 'clear') {
			setHistory([]);
			setInput('');
			return;
		}

		const output = evaluateCommand(trimmed);
		setHistory(prev => [...prev, { command: trimmed, output }]);
		setInput('');
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter') {
			e.preventDefault();
			submitCommand();
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (cmdLog.length === 0) return;
			const nextIndex = logPointer === null ? cmdLog.length - 1 : Math.max(0, logPointer - 1);
			setLogPointer(nextIndex);
			setInput(cmdLog[nextIndex]);
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (logPointer === null) return;
			const nextIndex = logPointer + 1;
			if (nextIndex >= cmdLog.length) {
				setLogPointer(null);
				setInput('');
			} else {
				setLogPointer(nextIndex);
				setInput(cmdLog[nextIndex]);
			}
		}
	};

	return (
		<div
			className="flex h-full flex-col bg-black font-mono text-[13px] leading-relaxed text-os-text"
			onClick={() => inputRef.current?.focus()}
		>
			<style>{`
				@keyframes term-cursor-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
				.term-cursor { animation: term-cursor-blink 1s steps(1, start) infinite; }
			`}</style>

			<div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
				{history.map((entry, i) => (
					<div key={i} className="mb-1">
						{entry.command !== null && (
							<div className="flex flex-wrap items-baseline gap-1.5">
								<span className="shrink-0 text-os-green">{PROMPT_USER}</span>
								<span className="shrink-0 text-os-textdim">:</span>
								<span className="shrink-0 text-os-accent">~</span>
								<span className="shrink-0 text-os-textdim">$</span>
								<span className="text-os-text">{entry.command}</span>
							</div>
						)}
						{entry.output.map((line, j) => renderLine(line, j))}
					</div>
				))}

				<div className="flex items-center gap-1.5">
					<span className="shrink-0 text-os-green">{PROMPT_USER}</span>
					<span className="shrink-0 text-os-textdim">:</span>
					<span className="shrink-0 text-os-accent">~</span>
					<span className="shrink-0 text-os-textdim">$</span>
					<div className="relative flex flex-1 items-center">
						<span className="whitespace-pre text-os-text">{input}</span>
						<span className="term-cursor ml-px inline-block h-[1.1em] w-[7px] translate-y-[1px] bg-os-text" />
						<input
							ref={inputRef}
							value={input}
							onChange={e => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							className="absolute inset-0 h-full w-full cursor-text opacity-0"
							autoFocus
							autoComplete="off"
							autoCapitalize="off"
							autoCorrect="off"
							spellCheck={false}
							aria-label="Terminal input"
						/>
					</div>
				</div>
				<div ref={bottomRef} />
			</div>
		</div>
	);
};

export default TerminalApp;
